import { encodeHash } from '../../utils/hashState';

/**
 * 跳转历史包裹查询。
 *
 * - 新标签页打开，当前页面不变；
 * - 只带入历史包裹查询也支持、且已填写的筛选条件（白名单）：去掉签收时间、异常信息一组（历史页没有）、
 *   WMS 仓库锚点（历史页按当前仓库自行注入）；
 * - 复用历史页现有的 URL hash 状态能力，附加 `_prefill=1`，历史页据此「只回填、不自动查询」。
 */
export type HistoryParcelSystem = 'HQ' | 'WMS' | 'QC';

export const HISTORY_PARCEL_PATHS: Record<HistoryParcelSystem, string> = {
    HQ: '/central/parcels/history',
    WMS: '/wms/parcels/history',
    QC: '/qualitycontrol/parcels/history',
};

export const HISTORY_PREFILL_FLAG = '_prefill';

/** 链接入口，用于埋点区分（PRD 第 6 节） */
export type HistoryLinkSource = 'deliveredAtHint' | 'deliveredAtAdjusted' | 'deliveredAtOutOfRange' | 'emptyResult' | 'partialNotFound';

export const CARRIED_FILTER_KEYS = [
    'trackingNumbers',
    'platformId',
    'channelId',
    'channelIds',
    'channelOrderNumber',
    'externalTrackingNumber',
    'orderTime',
    'hubId',
    'warehouseId',
    'sortingCodeSegment1',
    'sortingCodeSegment2',
    'sortingCodeSegment3',
    'receiverZipcode',
    'receiverZipcodeInput',
    'addrModified',
    'hasManded',
    'receivedAtHub',
    'deliverAt',
    'destHubArrivedAt',
    'destWarehouseArrivedAt',
    'overallStatus',
    'overallStatuses',
    'isComplaint',
    'podReviewStatus',
    'pickupHubId',
    'pickupHubIds',
    'lastDeliveryScanTime',
    'deliveredDspId',
    'deliveredDspIds',
    'driverAccountIds',
    'cooperationWarehouseId',
    'updatedAt',
    'bagTaskNumbers',
    'pickupWarehouseId',
    'prePickupHubName',
    'returnArrivalHubAt',
    'sortingChannelOrderNumber',
] as const;

const isFilled = (value: unknown): boolean => {
    if (value === undefined || value === null) return false;
    if (typeof value === 'string') return value.trim() !== '';
    if (Array.isArray(value)) return value.length > 0 && value.some((v) => v !== null && v !== undefined && v !== '');
    if (typeof value === 'object') return Object.values(value as object).some((v) => v !== null && v !== undefined);
    return true;
};

export function buildHistoryParcelHash(filters: Record<string, any> | null | undefined, override: Record<string, any> = {}): string {
    const source: Record<string, any> = { ...(filters ?? {}), ...override };
    const picked: Record<string, any> = {};
    CARRIED_FILTER_KEYS.forEach((key) => {
        if (isFilled(source[key])) {
            picked[key] = source[key];
        }
    });
    picked[HISTORY_PREFILL_FLAG] = 1;
    return encodeHash(picked);
}

export function buildHistoryParcelHref(
    system: HistoryParcelSystem,
    filters: Record<string, any> | null | undefined,
    override: Record<string, any> = {}
): string {
    return `${HISTORY_PARCEL_PATHS[system]}#${buildHistoryParcelHash(filters, override)}`;
}

/** 历史页：当前 hash 是否来自 R5 跳转 */
export const isHistoryPrefillHash = (hash: string): boolean =>
    new RegExp(`(^#?|&)${HISTORY_PREFILL_FLAG}=`).test(hash || '');
